import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchCurrentUser } from '../store/slices/authSlice';
import {
  hydrateFavorites,
  syncFavoritesOnAuth,
} from '../store/slices/favoritesSlice';
import { claimAllGuestThreads } from '../features/messaging/api/messagingApi';
import { messagingStore } from '../features/messaging/store';

/**
 * Hook to handle app initialization logic
 * Fetches user data once when app starts if user is authenticated
 */
export const useAppInitialization = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const hasInitialized = useRef(false);
  const wasAuthenticatedRef = useRef(isAuthenticated);

  // Hydrate favorites from localStorage exactly once on app start.
  // Runs even for unauthenticated visitors so a guest's saved posts survive
  // navigation and come along when they sign up as a buyer.
  useEffect(() => {
    dispatch(hydrateFavorites());
  }, [dispatch]);

  // One-time cleanup of legacy mock data in localStorage left over from
  // the pre-API mock service. Safe to remove this block after a few releases.
  useEffect(() => {
    try {
      ['mock:commissions:v1', 'mock:commissions:v2', 'mock:commissions:v3',
       'mock:commissions:v4', 'mock:commissions:v5',
       'mock:offer-templates:v1', 'mock:designer-last-visit:v1']
        .forEach(k => localStorage.removeItem(k));
    } catch {/* noop */}
  }, []);

  useEffect(() => {
    // Only initialize once
    if (hasInitialized.current) return;
    
    // Check for existing tokens in localStorage
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    // If we have tokens but not authenticated in Redux, verify them with the API
    if (accessToken && refreshToken && !isAuthenticated) {
      // Verify tokens are valid by fetching user data
      // Only after successful verification will isAuthenticated be set to true
      dispatch(fetchCurrentUser());
      hasInitialized.current = true;
    } else if (isAuthenticated && !user) {
      // If authenticated but no user data, fetch it
      dispatch(fetchCurrentUser());
      hasInitialized.current = true;
    }
  }, [dispatch, isAuthenticated, user]);

  // Merge any guest message threads onto the user the moment they authenticate
  // (covers email/password, OAuth, and persisted-session restore alike). Idempotent:
  // a second call simply sees an empty localStorage and returns 0.
  // Same auth transition also triggers the favorites sync (`wishlist_sync` flag
  // gates the actual server call inside the thunk — safe to dispatch always).
  useEffect(() => {
    const wasAuthed = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;
    if (!wasAuthed && isAuthenticated) {
      claimAllGuestThreads().catch((e) =>
        console.warn('[messaging] claimAllGuestThreads failed', e),
      );
      // Fire-and-forget: thunk silently no-ops when the user/flag isn't set up
      // for server sync, so it's safe to call on every transition.
      dispatch(syncFavoritesOnAuth());
    }
  }, [isAuthenticated, dispatch]);

  // Bootstrap the messaging store globally as soon as the user is authed.
  // This makes the unread badge in the sidebar / mobile bottom nav reflect
  // real counts everywhere — not just after the user has visited /messages.
  // Tear it down when the user signs out so subscriptions don't outlive
  // their session.
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      messagingStore.bootstrap({
        id: user.id,
        fullName: user.fullName || user.username || 'You',
        avatarUrl: user.profilePicture || null,
        isOnline: true,
      });
    } else if (!isAuthenticated) {
      messagingStore.dispose();
    }
  }, [isAuthenticated, user?.id, user?.fullName, user?.username, user?.profilePicture]);

  return { isAuthenticated, user };
};
